import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "./cart";
import { clientEnvironment } from "@/components/EnvironmentRibbon";
import { sp } from "@/store/nav";
import { Photo, ConditionBadge, money } from "./components";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Trash2, ShoppingCart, Truck } from "lucide-react";

export default function CartDrawer() {
  const { items, open, setOpen, remove, st } = useCart();
  const [val, setVal] = useState(null);
  const nav = useNavigate();
  const previewOffset = clientEnvironment() === "preview";
  useEffect(() => {
    if (!open) return;
    if (!items.length) return setVal(null);
    api.post("/store/cart/validate", { skus: items.map((i) => i.sku) }).then(({ data }) => setVal(data)).catch(() => setVal(null));
  }, [open, items]);
  const status = (sku) => val?.items?.find((x) => x.sku === sku);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className={`sl-store flex w-full flex-col bg-white p-0 text-slate-900 sm:max-w-md ${previewOffset ? "top-8 h-[calc(100%-2rem)]" : ""}`} data-testid="cart-drawer">
        <SheetHeader className="bg-[#0f2a3f] px-5 py-4"><SheetTitle className="flex items-center gap-2 font-heading text-lg text-white"><ShoppingCart className="h-5 w-5 text-emerald-400" /> {st("cart")} ({items.length})</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 && <div className="py-16 text-center text-sm text-slate-500" data-testid="cart-empty">{st("empty_cart")}</div>}
          {items.map((i) => {
            const s = status(i.sku);
            return (
              <div key={i.sku} className="flex gap-3 border-b border-slate-100 py-3" data-testid={`cart-item-${i.sku}`}>
                <Link to={sp(`/p/${i.slug}?unit=${i.sku}`)} onClick={() => setOpen(false)}><Photo id={i.photo} w={160} className="h-20 w-20 rounded-lg border border-slate-200" /></Link>
                <div className="min-w-0 flex-1">
                  <Link to={sp(`/p/${i.slug}?unit=${i.sku}`)} onClick={() => setOpen(false)} className="line-clamp-2 text-sm font-medium text-slate-900 hover:text-emerald-700">{i.title}</Link>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-500">{i.part_number} · {i.sku}</div>
                  <div className="mt-1 flex items-center gap-2"><ConditionBadge condition={i.condition} />{s && !s.ok && <span className="text-xs font-medium text-rose-600" data-testid={`cart-unavailable-${i.sku}`}>{s.reason === "not_listed" ? st("no_longer") : st("unit_reserved")}</span>}</div>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <div className="font-heading font-semibold">{money(s?.price ?? i.price)}</div>
                  <button data-testid={`cart-remove-${i.sku}`} onClick={() => remove(i.sku)} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" aria-label={st("remove")}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-4">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{st("subtotal")}</span><span className="font-heading text-xl font-semibold" data-testid="cart-subtotal">{money(val?.subtotal ?? items.reduce((a, b) => a + (b.price || 0), 0))}</span></div>
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><Truck className="h-3.5 w-3.5" /> {st("shipping_note")}</div>
            <button data-testid="checkout-btn" disabled={!val?.items?.some((x) => x.ok)} onClick={() => { setOpen(false); nav(sp("/checkout")); }} className="mt-4 w-full rounded-lg bg-[#0f2a3f] py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{st("checkout")}</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
